/**
 * Custom Icon Components
 *
 * Custom SVG icons that are not available in Lucide.
 * These follow the same API pattern as Lucide icons.
 */

import type { SVGProps } from 'react';

/**
 * Base props for custom icons (matching Lucide pattern)
 */
export interface CustomIconProps extends SVGProps<SVGSVGElement> {
  size?: number | string;
  strokeWidth?: number | string;
  absoluteStrokeWidth?: boolean;
}

/**
 * Helper to get icon dimensions
 */
function getIconDimensions(size: number | string = 24) {
  return { width: size, height: size };
}

/**
 * Tonnie Logo Icon (symbol only)
 */
export function TonnieIcon({ size = 24, className, ...props }: CustomIconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 48 48"
      fill="currentColor"
      className={className}
      {...getIconDimensions(size)}
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M24 4C12.954 4 4 12.954 4 24s8.954 20 20 20 20-8.954 20-20S35.046 4 24 4zm0 8c6.627 0 12 5.373 12 12s-5.373 12-12 12-12-5.373-12-12 5.373-12 12-12zm0 4a8 8 0 100 16 8 8 0 000-16z"
      />
    </svg>
  );
}

/**
 * Journey Miles Icon
 */
export function JourneyMilesIcon({
  size = 24,
  className,
  strokeWidth = 2,
  ...props
}: CustomIconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...getIconDimensions(size)}
      {...props}
    >
      <path d="M12 2L2 7l10 5 10-5-10-5z" />
      <path d="M2 17l10 5 10-5" />
      <path d="M2 12l10 5 10-5" />
    </svg>
  );
}

/**
 * Stamp Icon
 */
export function StampIcon({ size = 24, className, strokeWidth = 2, ...props }: CustomIconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...getIconDimensions(size)}
      {...props}
    >
      <rect x="3" y="11" width="18" height="10" rx="2" />
      <path d="M7 11V7a5 5 0 0110 0v4" />
      <circle cx="12" cy="16" r="2" />
    </svg>
  );
}

/**
 * Citizen Badge Icon
 */
export function CitizenBadgeIcon({
  size = 24,
  className,
  strokeWidth = 2,
  ...props
}: CustomIconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...getIconDimensions(size)}
      {...props}
    >
      <path d="M12 2L3 7v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

/**
 * LinkedIn Icon
 */
export function LinkedInIcon({ size = 24, className, ...props }: CustomIconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      {...getIconDimensions(size)}
      {...props}
    >
      <path d="M19 3a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h14m-.5 15.5v-5.3a3.26 3.26 0 00-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 011.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 001.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 00-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
    </svg>
  );
}

/**
 * Apple Icon
 *
 * The Apple logo glyph for "Sign in with Apple" buttons. Drawn with
 * `fill="currentColor"` so it inherits the button's text color (black
 * on the Apple-HIG dark button, white when inverted).
 */
export function AppleIcon({ size = 24, className, ...props }: CustomIconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      {...getIconDimensions(size)}
      {...props}
    >
      <path d="M17.05 12.536c-.025-2.564 2.094-3.793 2.189-3.853-1.192-1.744-3.045-1.982-3.704-2.008-1.578-.16-3.077.93-3.877.93-.8 0-2.032-.906-3.343-.882-1.72.025-3.305 1-4.19 2.54-1.787 3.1-.457 7.69 1.281 10.21.85 1.234 1.864 2.62 3.193 2.57 1.28-.05 1.764-.83 3.31-.83 1.547 0 1.983.83 3.343.805 1.38-.025 2.255-1.258 3.097-2.496.976-1.43 1.378-2.815 1.402-2.886-.03-.014-2.69-1.032-2.716-4.1zM14.49 4.96c.706-.857 1.183-2.047 1.053-3.234-1.017.041-2.25.678-2.98 1.534-.654.758-1.227 1.97-1.073 3.134 1.135.088 2.294-.577 3-1.434z" />
    </svg>
  );
}

/**
 * Google "G"
 *
 * Google's own four-colour mark, exactly as its sign-in branding guidelines
 * require — never recoloured, so the fills are the brand's and not tokens.
 */
export function GoogleIcon({ size = 24, className, ...props }: CustomIconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 48 48"
      className={className}
      {...getIconDimensions(size)}
      {...props}
    >
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/**
 * Microsoft logo
 *
 * The four squares in Microsoft's brand colours, as its sign-in branding
 * guidelines require — never recoloured.
 */
export function MicrosoftIcon({ size = 24, className, ...props }: CustomIconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 21 21"
      className={className}
      {...getIconDimensions(size)}
      {...props}
    >
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

/**
 * Export all custom icons
 */
export const CUSTOM_ICONS = {
  tonnie: TonnieIcon,
  journeyMiles: JourneyMilesIcon,
  stamp: StampIcon,
  citizenBadge: CitizenBadgeIcon,
  linkedin: LinkedInIcon,
  apple: AppleIcon,
  google: GoogleIcon,
  microsoft: MicrosoftIcon,
} as const;

export type CustomIconType = keyof typeof CUSTOM_ICONS;
